import React, { useState, useRef, useEffect } from 'react';
import styles from "@/styles/Typography.module.css";

/**
 * Searchable dropdown component with keyboard navigation
 * Supports both single and multiple selection modes
 * 
 * @component
 * @param {Object} props
 * @param {Array} props.options - Array of options to display
 * @param {string|Array} props.value - Currently selected value (string for single, array for multiple)
 * @param {Function} props.onChange - Callback when selection changes
 * @param {string} props.placeholder - Placeholder text
 * @param {Function} props.getLabel - Function to get label from option (default: option => option.name || option.label)
 * @param {Function} props.getValue - Function to get value from option (default: option => option.id || option.value)
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Disable the select
 * @param {boolean} props.multiple - Enable multiple selection with checkboxes
 * @param {number} props.maxHeight - Maximum height for dropdown list (default: 300)
 * @param {boolean} props.required - Mark field as required
 * @param {boolean} props.allowCreate - Allow creating new options by pressing Enter (for simple string values)
 * @param {('id'|'object')} props.returnValue - What to return in onChange: 'id' for just the ID (default), 'object' for the full option object
 */
export default function SearchableSelect({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Select...",
  getLabel = (option) => option.name || option.label || option,
  getValue = (option) => option.id || option.value || option,
  className = "",
  disabled = false,
  multiple = false,
  maxHeight = 300,
  required = false,
  allowCreate = false,
  returnValue = "id"
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Normalize value to array for multiple mode
  const selectedValues = multiple 
    ? (Array.isArray(value) ? value : []) 
    : (value ? [value] : []);

  // Filter options based on search term
  const filteredOptions = options.filter(option => 
    getLabel(option).toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get display text for selected value (single mode only)
  const getDisplayText = () => {
    if (multiple) {
      // In multiple mode, never show text in the input - keep it clear for typing
      return "";
    } else {
      const selectedOption = options.find(opt => getValue(opt) === value);
      // If option not found (e.g., newly created value), display the raw value
      return selectedOption ? getLabel(selectedOption) : value;
    }
  };

  const displayText = getDisplayText();

  // Get label for a selected value (handles both objects and strings)
  const getSelectedLabel = (val) => {
    const option = options.find(opt => getValue(opt) === val);
    return option ? getLabel(option) : val; // Fall back to raw value if not in options
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex(prev => 
            prev < filteredOptions.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        }
        break;
      case 'Enter':
        e.preventDefault();
        console.log('[SearchableSelect] Enter pressed:', { 
          isOpen, 
          highlightedIndex, 
          filteredOptionsLength: filteredOptions.length,
          allowCreate,
          searchTerm,
          multiple,
          selectedValues
        });
        if (isOpen && highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleSelect(filteredOptions[highlightedIndex]);
        } else if (allowCreate && searchTerm.trim() && multiple) {
          // Create new option in multiple mode
          const newValue = searchTerm.trim();
          console.log('[SearchableSelect] Creating new tag:', newValue);
          if (!selectedValues.includes(newValue)) {
            onChange([...selectedValues, newValue]);
          }
          setSearchTerm("");
          setHighlightedIndex(-1);
          inputRef.current?.focus();
        } else if (!isOpen) {
          setIsOpen(true);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm("");
        setHighlightedIndex(-1);
        break;
      default:
        if (!isOpen && e.key.length === 1) {
          setIsOpen(true);
        }
        break;
    }
  };

  // Handle option selection
  const handleSelect = (option) => {
    const optionValue = getValue(option);
    
    if (multiple) {
      // Toggle selection in multiple mode
      const newValues = selectedValues.includes(optionValue)
        ? selectedValues.filter(v => v !== optionValue)
        : [...selectedValues, optionValue];
      onChange(newValues);
      // Keep dropdown open in multiple mode
      setSearchTerm("");
      setHighlightedIndex(-1);
      inputRef.current?.focus();
    } else {
      // Single selection - return either the full object or just the ID based on returnValue prop
      const valueToReturn = returnValue === 'object' ? option : getValue(option);
      onChange(valueToReturn);
      setIsOpen(false);
      setSearchTerm("");
      setHighlightedIndex(-1);
    }
  };

  // Handle input focus
  const handleFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setHighlightedIndex(-1);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  return (
    <div ref={wrapperRef} className={`position-relative ${className}`}>
      <div className="input-group">
        <input
          ref={inputRef}
          type="text"
          className={`form-control ${disabled ? 'disabled' : ''}`}
          placeholder={multiple ? placeholder : (displayText || placeholder)}
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          required={required && selectedValues.length === 0}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label={placeholder || "Select an option"}
          autoComplete="off"
        />
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
          style={{ borderLeft: 0 }}
        >
          <span style={{ fontSize: '0.8em' }}>▼</span>
        </button>
      </div>

      {isOpen && (
        <div 
          className="position-absolute w-100 border bg-white shadow-sm"
          style={{ 
            zIndex: 1050, 
            maxHeight: `${maxHeight}px`, 
            overflowY: 'auto',
            marginTop: '2px',
            borderRadius: '4px'
          }}
          role="listbox"
          aria-roledescription="dropdown"
          aria-label="Select options"
        >
          {filteredOptions.length === 0 && searchTerm ? (
            <div className="p-2 text-muted">
              {allowCreate && multiple ? (
                <div className="text-center">
                  <div>No matching options</div>
                  <div className="text-primary mt-1">
                    <small>Press Enter to create "{searchTerm}"</small>
                  </div>
                </div>
              ) : (
                <div className="text-center">No options found</div>
              )}
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="p-2 text-muted text-center">
              No options available
            </div>
          ) : (
            filteredOptions.map((option, index) => {
              const optionValue = getValue(option);
              const isSelected = selectedValues.includes(optionValue);
              const isHighlighted = index === highlightedIndex;

              return (
                <div
                  key={optionValue}
                  className={`p-2 d-flex align-items-center ${isHighlighted ? 'bg-light' : ''} ${isSelected && !multiple ? 'bg-light' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  role="option"
                  aria-selected={isSelected}
                >
                  {multiple && (
                    <input
                      type="checkbox"
                      className="form-check-input me-2"
                      checked={isSelected}
                      onChange={() => {}} // Handled by parent div onClick
                      style={{ pointerEvents: 'none' }}
                      aria-label={getLabel(option)}
                    />
                  )}
                  <div className="d-flex w-100 justify-content-between align-items-center gap-2">
                    <span className="flex-shrink-0">{getLabel(option)}</span>
                    <span className="text-muted text-end" style={{ fontSize: "2rem" }}>{optionValue}</span>
                  </div>
                </div>
              );
            })
          )}
          
          {multiple && selectedValues.length > 0 && (
            <div className="border-top p-2 bg-light text-muted small" style={{fontSize: "var(--font-sm)" }}>
              {selectedValues.length} item{selectedValues.length !== 1 ? 's' : ''} selected
            </div>
          )}
        </div>
      )}
    </div>
  );
}
